(ns manage.adset-decision-tree-page.view.core
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["@xyflow/react" :refer [ReactFlowProvider useNodesInitialized]]
            ["react-reflex" :refer [ReflexContainer ReflexElement ReflexSplitter]]
            ["src/svg/artwork/map.svg$default" :as MapArtwork]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.patterns.empty-state-view :refer [empty-state-view]]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset.q]
            [common.url-template :as url-tpl]
            [common.widgets.overlay-page :as overlay-page]
            [manage.adset-decision-tree-page.editor.core :as editor]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.navigation :as navigation]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.context :as context]
            [manage.adset-decision-tree-page.view.header
             :refer [open-decision-tree-button
                     editor-header-actions
                     saving-indicator
                     strategy-tab-header]]
            [manage.adset-decision-tree-page.view.hook :as hook]
            [manage.adset-decision-tree-page.view.navigation-panel :as navigation-panel]
            [manage.adset-decision-tree-page.view.select-ad-variants-modal]
            [manage.adset-decision-tree-page.view.side-panel.core :refer [side-panel]]
            [manage.adset-decision-tree-page.view.table-util :as tu]
            [manage.adset-decision-tree-page.view.table.base :as base-table]
            [manage.adset-table.view :refer [optimizing-chip]]
            [manage.adset.hooks :as adset.h]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui empty-page-content []
  (let [url-context (use-subscribe [:url-context])]
    ($ empty-state-view
       {:title "Decision Tree Editor"
        :description "Unlock the power of decision-making!
                       Create a new tree from scratch or import an existing one.
                       Seamlessly design and analyze your strategies with our
                       intuitive Decision Tree Editor."
        :artwork MapArtwork
        :docs-href (url-tpl/render :docs/article (assoc url-context :slug "23873462803740-Building-your-Decision-Tree"))
        :actions ($ :<>
                    ($ open-decision-tree-button)
                    ($ Button {:variant "outlined"
                               :disabled true}
                       "Import"))})))

(defn- error-code->dialog-content [error-message]
  (get {"a decision tree should have at least one assigned variant"
        {:title-message "Assigned variant missing"
         :body-message (str "Before you can publish this targeting configuration, please ensure that you have at least one assigned variant. "
                            "Make sure to review and link at least one variant to your decision tree nodes before attempting to publish.")}}
       error-message
       {:title-message "Unknown error"
        :body-message error-message}))

(defmodal publishing-error ::publishing-error-modal
  [{:keys [error-message]}]
  (let [{:keys [title-message body-message]} (error-code->dialog-content error-message)]
    ($ modal-v2
       {:icon ($ SvgIcon {:color "warning"}
                 ($ AlertIcon))
        :title title-message
        :footer ($ Button {:variant "contained"
                           :color "warning"
                           :on-click #(rf/dispatch [:modal/close])}
                   "Resume editing")}
       ($ Typography {:sx #js {:maxWidth 600}} body-message))))

(defui editor-layout [{:keys [read-only] :as props}]
  ($ ReflexContainer {:orientation "vertical"
                      :windowResizeAware true}
     ($ ReflexElement
        ($ editor/root-view))
     ($ ReflexSplitter)
     ($ ReflexElement {:size 410
                       :minSize 200
                       :maxSize 500}
        (if read-only
          ($ navigation-panel/panel props)
          ($ side-panel)))))

(def offline-error-handler (partial event/generic-error-handler {:offline true}))

(defhook use-editor-lifecycle []
  (let [{:keys [is-editor-readonly properties-context]
         {draft-decision-tree-id :id} :draft
         {published-decision-tree-id :id} :published}
        (uix/use-context context/decision-trees-context)
        decision-tree-id (or draft-decision-tree-id published-decision-tree-id)
        {editor-state-is-initialized :editor-is-initialized
         initialization-error-details :error
         initialization-error :isError
         initialization-is-fetching :isFetching}
        (hook/use-editor-state {:decision-tree-id decision-tree-id
                                :read-only is-editor-readonly})

        url-context (use-subscribe [:url-context])
        reconnection-handler (uix/use-callback
                              (fn []
                                (rf/dispatch [:modal/close])
                                (hook/clean-editor
                                 (assoc url-context :decision-tree-id decision-tree-id)))
                              [url-context decision-tree-id])

        is-saving-fetching (= :fetching (use-subscribe [::sub/decision-tree-saving-status]))
        nodes-inititialized (useNodesInitialized)
        is-properties-context-loaded (some? properties-context)
        editor-is-initialized (and nodes-inititialized
                                   editor-state-is-initialized
                                   is-properties-context-loaded)]
    (uix/use-effect (fn []
                      (.addEventListener js/window "offline" offline-error-handler)
                      (.addEventListener js/window "online" reconnection-handler)
                      (fn []
                        (.removeEventListener js/window "offline" offline-error-handler)
                        (.removeEventListener js/window "online" reconnection-handler)))
                    [reconnection-handler])
    (uix/use-effect (fn []
                      (when is-saving-fetching
                        (rf/dispatch [:loader/open])
                        #(rf/dispatch [:loader/close])))
                    [is-saving-fetching])
    (uix/use-effect #(when initialization-is-fetching (rf/dispatch [:modal/close]))
                    [initialization-is-fetching])
    (uix/use-effect #(when initialization-error
                       (event/generic-error-handler initialization-error-details))
                    [initialization-error initialization-error-details])
    (uix/use-effect
     (fn []
       (when-not editor-is-initialized
         (rf/dispatch [:loader/open])
         ;; Show the loader immediately, because after reactflow started rendering the nodes
         ;; (which can take longer when having 100+ nodes) the loader won't eventually be
         ;; rendered
         (rf/dispatch [:loader/show])
         #(rf/dispatch [:loader/close])))
     [editor-is-initialized])))

(defui editor-overlay []
  (let [url-context (use-subscribe [:url-context])
        {{:keys [test-state]} :data} (use-query (adset.q/adset-aco-active-configuration url-context))
        {:keys [is-editor-readonly]} (uix/use-context context/decision-trees-context)]
    (use-editor-lifecycle)
    ($ overlay-page/overlay-page
       ($ overlay-page/header
          {:on-close #(rf/dispatch [::event/schedule-after-nodes-are-saved navigation/exit-to-table])
           :title "Decision Tree"}
          ($ Stack {:direction "row"
                    :justifyContent "space-between"
                    :alignItems "center"
                    :sx #js {:flex 1}}
             ($ Stack {:direction "row" :gap 1}
                (when (= test-state "started") ($ optimizing-chip))
                (when-not is-editor-readonly ($ saving-indicator)))
             ($ editor-header-actions)))
       ($ Stack {:sx #js {:flex 1 :minHeight 0}}
          ($ editor-layout {:full-page true
                            :read-only is-editor-readonly})))))

(defui strategy-tab-view []
  (let [{is-trees-query-success :is-trees-query-success
         {published-decision-tree-id :id} :published}
        (uix/use-context context/decision-trees-context)
        adset-channel (adset.h/use-channel)
        view-mode (use-subscribe [::sub/view-mode])
        {:keys [editor-is-initialized]}
        (hook/use-editor-state {:read-only true
                                :decision-tree-id published-decision-tree-id})]
    (when is-trees-query-success
      (if (nil? published-decision-tree-id)
        ($ empty-page-content)
        ($ :<>
           ($ strategy-tab-header)
           (if (= view-mode "tree")
             (if editor-is-initialized
               ($ editor-layout {:read-only true})
               ($ Stack {:alignItems "center"
                         :justifyContent "center"
                         :sx #js {:width "100%" :height "100%"}}
                  ($ CircularProgress)))
             ($ Stack {:sx #js {:flex 1 :minHeight 0}}
                ($ tu/activation-procession-loading-alert)
                (case adset-channel
                  "meta" ($ base-table/meta-table)
                  "tiktok" ($ base-table/tiktok-table)
                  ($ base-table/base-table)))))))))

(defui strategy-view []
  (let [view-mode (use-subscribe [::sub/view-mode])]
    ($ ReactFlowProvider
       (if (= view-mode "editor")
         ($ editor-overlay)
         ($ strategy-tab-view)))))
